import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError, NETWORK_ERROR_MESSAGE } from '../../store/slices/authSlice';
import { AuthBrandingPanel, AuthMobileHeader } from './AuthBrandingPanel';
import LoginConnectivityPopup from './LoginConnectivityPopup';
import './LoginConnectivityPopup.css';
import { describeLoginConnectivityFailure } from '../../services/loginConnectivityCheck';
import {
  getRememberedAccounts,
  saveRememberedAccount,
  removeRememberedAccount,
  isRememberMePreferred,
  setRememberMePreferred,
  resolveRememberedAvatar,
  hydrateRememberedAccounts,
} from '../../services/rememberedAccounts';

const initialsOf = (account) => {
  const first = (account?.firstName || account?.displayName || account?.username || '?').trim();
  return first.charAt(0).toUpperCase();
};

const RememberedAvatar = ({ account, textClassName = '' }) => {
  const [broken, setBroken] = React.useState(false);
  const src = !broken ? resolveRememberedAvatar(account) : null;
  if (src) {
    return (
      <span className="relative block h-full w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      </span>
    );
  }
  return (
    <span className={`login-rainbow-avatar flex h-full w-full items-center justify-center rounded-full text-white ${textClassName}`}>
      {initialsOf(account)}
    </span>
  );
};

const PasswordToggle = ({ show, onToggle, light = false }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 ${
      light
        ? 'text-white/80 hover:text-white focus:ring-white/70'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-primary-500'
    }`}
    aria-label={show ? 'Hide password' : 'Show password'}
    tabIndex={-1}
  >
    {show ? (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" />
        <path d="M3 21L20 4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5C8.24261 5 5.43602 7.4404 3.76737 9.43934C2.51521 10.9394 2.51521 13.0606 3.76737 14.5607C5.43602 16.5596 8.24261 19 12 19C15.7574 19 18.564 16.5596 20.2326 14.5607C21.4848 13.0606 21.4848 10.9394 20.2326 9.43934C18.564 7.4404 15.7574 5 12 5Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const Login = () => {
  const rememberedOnLoad = getRememberedAccounts();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => isRememberMePreferred());
  const [rememberedAccounts, setRememberedAccounts] = useState(rememberedOnLoad);
  const [view, setView] = useState(rememberedOnLoad.length ? 'welcome' : 'form');
  const [askPassword, setAskPassword] = useState(false);
  const [showConnectivityPopup, setShowConnectivityPopup] = useState(false);
  const [connectionReason, setConnectionReason] = useState(null);
  const passwordRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, user } = useSelector((state) => state.auth);
  const isNetworkError = error === NETWORK_ERROR_MESSAGE;
  const lastUser = rememberedAccounts[0] || null;

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from;
      const dest = from?.pathname
        ? `${from.pathname}${from.search || ''}${from.hash || ''}`
        : '';
      if (dest && dest !== '/login') {
        navigate(dest, { replace: true });
        return;
      }
      if (user?.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/chat');
      }
    }
  }, [isAuthenticated, navigate, user, location.state]);

  useEffect(() => {
    if (isNetworkError) {
      setShowConnectivityPopup(true);
    }
  }, [isNetworkError]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (askPassword && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [askPassword]);

  useEffect(() => {
    let cancelled = false;
    hydrateRememberedAccounts().then((list) => {
      if (cancelled) return;
      setRememberedAccounts(list);
      if (list.length) {
        setView((current) => (current === 'form' ? 'welcome' : current));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConnectionReason(null);
    const keepRemembered = rememberMe || view === 'welcome';
    setRememberMePreferred(keepRemembered);
    try {
      const payload = await dispatch(login({
        username: formData.username,
        password: formData.password,
      })).unwrap();
      if (keepRemembered) {
        await saveRememberedAccount(payload);
      }
    } catch {
      // Error is shown from auth slice
    }
  };

  const handlePickRemembered = (account) => {
    setFormData((prev) => ({ ...prev, username: account.username, password: '' }));
  };

  const handleForgetAccount = (event, username) => {
    event.stopPropagation();
    removeRememberedAccount(username);
    const next = getRememberedAccounts();
    setRememberedAccounts(next);
    if (!next.length) {
      setView('form');
      setAskPassword(false);
    }
  };

  const openLastUserPassword = () => {
    if (!lastUser) return;
    setFormData({ username: lastUser.username, password: '' });
    setAskPassword(true);
    dispatch(clearError());
  };

  const goToOtherUser = () => {
    setView('form');
    setAskPassword(false);
    setFormData({ username: '', password: '' });
    dispatch(clearError());
  };

  const credentialError = Boolean(error && !isNetworkError);
  const errorLabel = (() => {
    if (!credentialError) return null;
    const lowered = String(error).toLowerCase();
    if (lowered.includes('bad credentials') || lowered.includes('unauthorized')) {
      return 'Invalid credentials';
    }
    return String(error).replace(/^error:\s*/i, '');
  })();

  const errorBanner = errorLabel ? (
    <div className="login-error-banner" role="alert">
      <span className="login-error-icon" aria-hidden>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </span>
      <span>{errorLabel}</span>
    </div>
  ) : null;

  return (
    <div className="h-screen flex overflow-hidden">
      <AuthBrandingPanel />

      <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="w-full max-w-md">
          <AuthMobileHeader />

          {view === 'welcome' && lastUser ? (
            <div className="login-welcome-card relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-xl p-8 border border-gray-200 dark:border-gray-700">
              <div className="relative z-10 text-center">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 tracking-wide uppercase">
                  Welcome back
                </p>
                <button
                  type="button"
                  onClick={openLastUserPassword}
                  className="login-welcome-photo mx-auto mt-5 block rounded-full focus:outline-none"
                  aria-label={`Continue as ${lastUser.displayName || lastUser.username}`}
                >
                  <span className="login-rainbow-avatar login-welcome-avatar-ring flex h-28 w-28 items-center justify-center rounded-full p-[4px] shadow-lg">
                    <RememberedAvatar account={lastUser} textClassName="text-4xl font-bold" />
                  </span>
                </button>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {lastUser.displayName || lastUser.username}
                </h2>
                <p className="mt-1 text-gray-500 dark:text-gray-400 text-sm">
                  {lastUser.email || `@${lastUser.username}`}
                </p>
                {!askPassword && (
                  <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                    Tap your photo to enter password
                  </p>
                )}

                {askPassword && (
                  <form className="mt-6 text-left space-y-4" onSubmit={handleSubmit}>
                    <div className="relative">
                      <input
                        ref={passwordRef}
                        id="welcome-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        className={`w-full px-4 py-3 pr-11 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          credentialError
                            ? 'border border-red-400 focus:ring-red-400'
                            : 'border border-gray-300 dark:border-gray-600 focus:ring-rose-400'
                        }`}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <PasswordToggle show={showPassword} onToggle={() => setShowPassword((prev) => !prev)} />
                    </div>
                    {errorBanner}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                    >
                      {loading ? 'Signing in...' : 'Sign in'}
                    </button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={goToOtherUser}
                  className="mt-5 w-full py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Login with other user
                </button>
              </div>
            </div>
          ) : (
            <div className="login-form-card relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
              <div className="relative mb-8">
                <h2 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
                  Welcome back
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Sign in to continue to WorkLoop
                </p>
                {rememberedAccounts.length > 0 && (
                  <div className="mt-5 flex justify-start overflow-x-auto pt-3 pb-2 px-1">
                    <div className="flex items-start">
                      {rememberedAccounts.map((account, index) => (
                        <button
                          key={account.username}
                          type="button"
                          onClick={() => handlePickRemembered(account)}
                          title={`${account.displayName} (@${account.username})`}
                          className="login-remember-chip relative flex flex-col items-center w-[3.25rem] group"
                          style={{
                            marginLeft: index === 0 ? 0 : -12,
                            zIndex: rememberedAccounts.length - index,
                            animationDelay: `${index * 80}ms`,
                          }}
                        >
                          <span className="relative">
                            <span className="login-rainbow-avatar flex h-12 w-12 items-center justify-center rounded-full p-[2px] shadow-md">
                              <RememberedAvatar account={account} textClassName="text-lg font-semibold" />
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(event) => handleForgetAccount(event, account.username)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  handleForgetAccount(event, account.username);
                                }
                              }}
                              className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-gray-800 text-white text-[10px] leading-none opacity-0 group-hover:opacity-100"
                              aria-label={`Remove ${account.displayName}`}
                            >
                              ×
                            </span>
                          </span>
                          <span className="mt-1.5 max-w-[3.25rem] truncate text-[11px] font-medium text-gray-700 dark:text-gray-300">
                            {account.firstName || account.displayName || account.username}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <form className="relative space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Username
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        className={`w-full px-4 py-3 pr-11 rounded-lg focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all ${
                          credentialError
                            ? 'border border-red-400 focus:ring-red-400 focus:border-red-400'
                            : 'border border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-transparent'
                        }`}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <PasswordToggle show={showPassword} onToggle={() => setShowPassword((prev) => !prev)} />
                    </div>
                  </div>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => {
                      const next = event.target.checked;
                      setRememberMe(next);
                      setRememberMePreferred(next);
                    }}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Remember me</span>
                </label>

                {errorBanner}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </div>

                <div className="text-center space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <Link
                      to="/register"
                      className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors"
                    >
                      Create Account
                    </Link>
                    <span className="mx-2 text-gray-400 dark:text-gray-500" aria-hidden>
                      |
                    </span>
                    <Link
                      to="/password-reset-request"
                      className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors"
                    >
                      Forgot Password ?
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          )}

          {connectionReason && (
            <div className="mt-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3.5">
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                {connectionReason.title}
              </p>
              <p className="mt-1 text-sm text-red-600 dark:text-red-200 leading-relaxed">
                {connectionReason.message}
              </p>
            </div>
          )}
        </div>
      </div>
      <LoginConnectivityPopup
        open={showConnectivityPopup}
        onComplete={(results) => {
          setConnectionReason(describeLoginConnectivityFailure(results));
        }}
        onClose={() => {
          setShowConnectivityPopup(false);
          dispatch(clearError());
        }}
      />
    </div>
  );
};

export default Login;
